import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Apollo } from 'src/gql/generated';

export type MentionEntitiesResult = Apollo.GetMentionEntitiesQuery['mentionEntities'];
export type DocumentMentionAccessResult = MentionEntitiesResult['documents'][number];
export type PlaybookMentionEntity = MentionEntitiesResult['playbooks'][number];
export type PlaybookRuleMentionEntity = MentionEntitiesResult['playbookRules'][number];
export type TaskMentionEntity = MentionEntitiesResult['tasks'][number];
export type WorkflowMentionEntity = MentionEntitiesResult['workflows'][number];
export type ReportMentionEntity = MentionEntitiesResult['reports'][number];
export type SkillVersionMentionEntity = MentionEntitiesResult['skillVersions'][number];
export type MentionAccessError = Extract<
  DocumentMentionAccessResult,
  { __typename: 'MentionAccessError' }
>;

export interface MentionEntityContextValue {
  documents: Map<string, DocumentMentionAccessResult>;
  playbookRules: Map<string, PlaybookRuleMentionEntity>;
  playbooks: Map<string, PlaybookMentionEntity>;
  reports: Map<string, ReportMentionEntity>;
  skillVersions: Map<string, SkillVersionMentionEntity>;
  tasks: Map<string, TaskMentionEntity>;
  workflows: Map<string, WorkflowMentionEntity>;
}

const EMPTY_MENTION_ENTITY_CONTEXT_VALUE: MentionEntityContextValue = {
  documents: new Map(),
  playbookRules: new Map(),
  playbooks: new Map(),
  reports: new Map(),
  skillVersions: new Map(),
  tasks: new Map(),
  workflows: new Map(),
};

/**
 * Context for sharing hydrated mention entities from parent to MentionElement children.
 *
 * For accessible documents: contains full Document with processing status fields.
 * For inaccessible/deleted mentions: contains MentionAccessError with reason.
 * For accessible mentions: contains authorized entities returned by mentionEntities.
 *
 * ChatPanel provides context for historical messages.
 * ChatPanelInput provides context for current editor mentions.
 */
export const MentionEntityContext = createContext<MentionEntityContextValue>(
  EMPTY_MENTION_ENTITY_CONTEXT_VALUE
);

/**
 * Hook to get a specific document's access result from context.
 * Returns Document if accessible, MentionAccessError if not, or null if not in context.
 */
export function useDocMentionAccessContext(documentId: string): DocumentMentionAccessResult | null {
  const { documents } = useContext(MentionEntityContext);
  return documents.get(documentId) ?? null;
}

export function useTaskMentionEntity(taskId: string): TaskMentionEntity | null {
  const { tasks } = useContext(MentionEntityContext);
  return tasks.get(taskId) ?? null;
}

export function useWorkflowMentionEntity(workflowId: string): WorkflowMentionEntity | null {
  const { workflows } = useContext(MentionEntityContext);
  return workflows.get(workflowId) ?? null;
}

export function useReportMentionEntity(reportId: string): ReportMentionEntity | null {
  const { reports } = useContext(MentionEntityContext);
  return reports.get(reportId) ?? null;
}

export function usePlaybookMentionEntity(playbookId: string): PlaybookMentionEntity | null {
  const { playbooks } = useContext(MentionEntityContext);
  return playbooks.get(playbookId) ?? null;
}

export function usePlaybookRuleMentionEntity(
  playbookRuleId: string
): PlaybookRuleMentionEntity | null {
  const { playbookRules } = useContext(MentionEntityContext);
  return playbookRules.get(playbookRuleId) ?? null;
}

/**
 * Context for sharing access request status from parent to MentionElement children.
 * Maps document IDs to their access request status (PENDING, ACCEPTED, REJECTED).
 */
export const AccessRequestStatusContext = createContext<{
  refetch: () => void;
  statusMap: Map<string, Apollo.AccessRequestStatus>;
  updateStatus: (newStatuses: AccessRequestInfo[]) => void;
}>({ refetch: () => {}, statusMap: new Map(), updateStatus: () => {} });

/**
 * Hook to check if an access request is pending for a document.
 */
export function useAccessRequestStatusContext(documentId: string): {
  isPending: boolean;
  refetch: () => void;
  status: Apollo.AccessRequestStatus | null;
  updateStatus: (newStatuses: AccessRequestInfo[]) => void;
} {
  const { refetch, statusMap, updateStatus } = useContext(AccessRequestStatusContext);
  const status = statusMap.get(documentId) ?? null;
  const isPending = status === Apollo.AccessRequestStatus.Pending;

  return { isPending, refetch, status, updateStatus };
}

/** Access request info returned from mutation */
export type AccessRequestInfo = { documentId: string; status: Apollo.AccessRequestStatus };

/**
 * Hook to check access request status for documents from the server.
 * Returns a map of document ID -> status (PENDING, ACCEPTED, REJECTED).
 *
 * This replaces the localStorage-based tracking with server-side persistence.
 * Use `updateStatus` to merge new status data (e.g., from mutation response)
 * without making an additional network request.
 */
export function useAccessRequestStatus(documentIds: string[]): {
  loading: boolean;
  refetch: () => void;
  statusMap: Map<string, Apollo.AccessRequestStatus>;
  /** Merge new status data into the map (avoids refetch) */
  updateStatus: (newStatuses: AccessRequestInfo[]) => void;
} {
  const { data, loading, refetch } = Apollo.useGetMyAccessRequestsForDocumentsQuery({
    skip: documentIds.length === 0,
    variables: { documentIds },
  });
  // Keep previous map during loading to prevent flicker when documentIds change
  const previousMapRef = useRef<Map<string, Apollo.AccessRequestStatus>>(new Map());
  // Track manually added statuses (from mutation responses)
  const [manualStatuses, setManualStatuses] = useState<AccessRequestInfo[]>([]);

  const statusMap = useMemo(() => {
    const map = new Map<string, Apollo.AccessRequestStatus>();
    // First, add data from query
    if (data?.myAccessRequestsForDocuments) {
      for (const doc of data.myAccessRequestsForDocuments) {
        map.set(doc.documentId, doc.status);
      }
    }
    // Then merge manual statuses (these take precedence as they're more recent)
    for (const status of manualStatuses) {
      map.set(status.documentId, status.status);
    }
    if (map.size > 0) {
      previousMapRef.current = map;
      return map;
    }
    // During loading with no data, return previous map to prevent flicker
    return previousMapRef.current;
  }, [data, manualStatuses]);

  const updateStatus = useCallback((newStatuses: AccessRequestInfo[]) => {
    setManualStatuses(prev => {
      // Merge new statuses, replacing any existing entries for the same documentId
      const merged = new Map(prev.map(s => [s.documentId, s]));
      for (const status of newStatuses) {
        merged.set(status.documentId, status);
      }
      return Array.from(merged.values());
    });
  }, []);

  return { loading, refetch, statusMap, updateStatus };
}
